import { Navigate } from "react-router-dom";
import { AuthCard } from "../components/AuthCard";
import { AdminDashboard } from "../components/admin/AdminDashboard";
import { useSession } from "../lib/useSession";

/**
 * The gate in front of the dashboard. Not authenticated goes back to /login,
 * carrying the way back so signing in lands here instead of the ordinary
 * post-login destination; signed in without the `admin` group gets told
 * plainly rather than bounced silently, because unlike a project's 404 this
 * page's existence is not a secret — only what is behind it is.
 *
 * The real gate is server-side: every call the dashboard makes goes through
 * require_admin (apps/api/src/app/routes/admin.py), which is what actually
 * decides who can read or write anything here. This component only decides
 * what to render.
 */
export function AdminPage() {
  const session = useSession();

  if (session.isPending) {
    return (
      <AuthCard title="AI Labs — Admin" subtitle="Checking your session">
        <div className="h-24 animate-pulse rounded-lg bg-adriatic-deep/5" aria-hidden="true" />
        <span className="sr-only">Checking your session</span>
      </AuthCard>
    );
  }

  if (session.isError) {
    return (
      <AuthCard title="Admin is unavailable" subtitle="The service did not respond">
        <p role="alert" className="text-sm text-adriatic-mid">
          This is a fault on our side rather than a problem with your account. If it persists, tell
          the AI Labs administrator.
        </p>
      </AuthCard>
    );
  }

  if (!session.data.authenticated) {
    return <Navigate to="/login?redirect_uri=/admin" replace />;
  }

  if (!session.data.user.roles.includes("admin")) {
    return (
      <AuthCard title="Not available" subtitle={`Signed in as ${session.data.user.email}`}>
        <p className="text-sm text-adriatic-mid">
          The admin dashboard is for the admin group. If you believe this is wrong, ask an
          administrator.
        </p>
      </AuthCard>
    );
  }

  return <AdminDashboard user={session.data.user} />;
}
